import { useReducedMotion } from "framer-motion"
import { useCallback } from "react"

/**
 * Hook that returns the appropriate scroll behavior based on user's motion preferences.
 * Returns "auto" if user prefers reduced motion, "smooth" otherwise.
 */
export const useScrollBehavior = (): ScrollBehavior => {
  const shouldReduceMotion = useReducedMotion()
  return shouldReduceMotion ? "auto" : "smooth"
}

/**
 * Hook that provides an accessible scrollTo function respecting prefers-reduced-motion.
 *
 * @example
 * const scrollTo = useAccessibleScrollTo()
 * scrollTo({ top: 0 }) // Respects user's motion preference
 * scrollTo({ top: 100, left: 0 }) // Custom scroll position
 */
export const useAccessibleScrollTo = () => {
  const scrollBehavior = useScrollBehavior()

  return useCallback(
    (options: ScrollToOptions) => {
      window.scrollTo({
        ...options,
        behavior: scrollBehavior,
      })
    },
    [scrollBehavior]
  )
}

/**
 * Utility function to get the appropriate scroll behavior.
 * Use this when you can't use hooks (e.g., in event handlers outside React components).
 */
export const getScrollBehavior = (): ScrollBehavior => {
  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches
  return prefersReducedMotion ? "auto" : "smooth"
}

/**
 * Accessible scrollTo function that respects prefers-reduced-motion.
 * Use this when you can't use the hook.
 *
 * @example
 * accessibleScrollTo({ top: 0 })
 * accessibleScrollTo({ top: 100, left: 0 })
 */
export const accessibleScrollTo = (options: ScrollToOptions) => {
  window.scrollTo({
    ...options,
    behavior: getScrollBehavior(),
  })
}

// Default export for backward compatibility
export default accessibleScrollTo
